'use client';

import { BACKEND_URL } from '@/lib/config';
import type { DataSourceInput } from '@/types/dataSources';
import type { NativeMessageDTO } from '@/types/history-types';
import { languageEnum } from '@/types/language';
import type { ThinkingMode } from '@/types/thinkingTypes';
import ApiService, { type ApiResponse } from './api-services';

export interface TaskStatusResponse {
  session_id: string;
  progress: {
    step: string;
    progress: number;
    status: string;
    event: string;
    data: string;
    seq: number | null;
  };
}

interface StartResearchPayload {
  query: string;
  session_id: string;
  local: string;
  filters: any;
  thinking_mode: ThinkingMode; // 'fast' | 'thinking' | 'deep'
  data_sources: DataSourceInput[];
  retry?: boolean | undefined; // If true, this is a retry - clear Redis events before starting
  retry_chat_id?: number | undefined; // Idempotency key: ID of existing message to reuse on retry
  // Search page flow parameters
  flow_type?:
    | 'research'
    | 'search_page'
    | 'document_analysis'
    | 'document_generation'
    | undefined;
  page?: number | undefined;
  page_size?: number | undefined;
  sort_field?: string | undefined;
  sort_direction?: 'asc' | 'desc' | undefined;
  // Issue #428: Smart filter toggle
  auto_filter_enabled?: boolean | undefined;
  // Reference date for document analysis (ISO "YYYY-MM-DD")
  reference_date?: string | undefined;
  // Enable ReAct agent (v3) for this request
  v3?: boolean | undefined;
  // Multi-perspective analysis lens (Issue #1799)
  perspective?: string | undefined;
  // Selected context-chip ids (Spisy, #6269) — scopes retrieval to the user's
  // chosen files. Empty/omitted = no chip scope (general corpus).
  context_chips?: number[] | undefined;
}

class ResearchServices extends ApiService {
  constructor() {
    super(BACKEND_URL.RESEARCH);
  }

  postResearch({
    question,
    chat_lang = languageEnum.SK,
    prompts_lang = languageEnum.SK,
    local,
    session_id,
    filters,
    debug = false,
    web_search = false,
  }: {
    question: string;
    chat_lang?: languageEnum;
    prompts_lang?: languageEnum;
    local: string;
    session_id: string;
    filters?: any;
    debug?: boolean;
    web_search?: boolean;
  }): ApiResponse<string> {
    return this.postData('', {
      question,
      chat_lang,
      prompts_lang,
      local,
      session_id,
      filters,
      debug,
      web_search,
    });
  }

  getSessionsHistory(
    type:
      | 'chat'
      | 'search'
      | 'document_analysis'
      | 'document_generation' = 'chat',
    options?: { limit?: number; cursor?: number; case_id?: number }
  ): ApiResponse<
    {
      session_id: string;
      created_at: string;
      title: string | null;
      sess_id: number;
      access_role?: 'owner' | 'shared_direct' | 'shared_group';
      /** Count of active share grants for owned sessions. */
      share_count?: number;
      /** Display name of the session owner for shared-with-me sessions. */
      owner_name?: string | null;
      /** Organization ID if session belongs to an org, null for personal. */
      org_id?: string | null;
      /** Case (spis) ID this session was started from, null for general sessions (#6272). */
      case_id?: number | null;
    }[]
  > {
    return this.postData('/history', {
      type,
      ...(options?.limit !== undefined && { limit: options.limit }),
      ...(options?.cursor !== undefined && { cursor: options.cursor }),
      ...(options?.case_id !== undefined && { case_id: options.case_id }),
    });
  }

  /**
   * Get session messages in native format (flat list with role/content/status).
   * One message per row, no legacy user/model grouping.
   *
   * @param session_id - The session ID to fetch messages for
   * @param options.limit - Number of messages to fetch (default: 50, max: 200)
   * @param options.cursor - Message ID to fetch messages BEFORE (undefined = fetch latest)
   */
  getSessionHistory(
    session_id: string,
    options?: { limit?: number; cursor?: number }
  ): ApiResponse<NativeMessageDTO[]> {
    const params = new URLSearchParams();
    if (options?.limit !== undefined) {
      params.set('limit', options.limit.toString());
    }
    if (options?.cursor !== undefined) {
      params.set('cursor', options.cursor.toString());
    }
    const queryString = params.toString();
    const url = `/history/${session_id}${queryString ? `?${queryString}` : ''}`;
    return this.getData(url);
  }

  deleteNextResearchMessages(data: {
    session_id: string;
    message_id: number;
  }): ApiResponse<any> {
    return this.deleteData('', data);
  }

  // Method to check task status
  getTaskStatus(sessionId: string): ApiResponse<TaskStatusResponse> {
    return this.getData(`/task-status/${sessionId}`);
  }

  /**
   * `options.timeoutMs` bounds the request (axios `timeout` config) and
   * `options.rejectOn403` makes a plain 403 (no `mfa_required` code) REJECT
   * instead of the shared interceptor's default resolve-with-data — both
   * used by `useResearchStream`'s `stopStream` on its await-and-verify path
   * (#11755 follow-up) so a hung request can't leave Stop pending forever,
   * and so a denied caller's `catch` block actually runs (a real browser
   * check caught the interceptor resolving a plain 403, making that catch
   * unreachable even though every mocked-service unit test passed). Both
   * omitted entirely for the fire-and-forget (known-writable caller) path,
   * which never awaits this call at all.
   */
  cancelResearch(
    sessionId: string,
    options?: { timeoutMs?: number; rejectOn403?: boolean }
  ): ApiResponse<void> {
    const axiosOptions = {
      ...(options?.timeoutMs !== undefined && { timeout: options.timeoutMs }),
      ...(options?.rejectOn403 && { rejectOn403: true }),
    };
    return this.postData(
      `/cancel/${sessionId}`,
      {},
      Object.keys(axiosOptions).length > 0 ? axiosOptions : undefined
    );
  }

  /**
   * `options.rejectOn403` mirrors `cancelResearch`'s opt-in (#11755) — makes
   * a plain 403 (no `mfa_required` code) REJECT instead of the shared
   * interceptor's default resolve-with-data, so `startStream`'s 402/403/409
   * `catch` branch (quota-exceeded overflow prompt) actually runs for a real
   * `DOC_GEN_MONTHLY_LIMIT_REACHED` 403 (#12205) rather than being dead code
   * reachable only in mocked-service tests.
   */
  startResearch(
    payload: StartResearchPayload,
    options?: { rejectOn403?: boolean }
  ): ApiResponse<any> {
    return this.postData(
      '',
      payload,
      options?.rejectOn403 ? { rejectOn403: true } : undefined
    );
  }

  /**
   * Create a research session from search results.
   * Bridges the search page and research chat by pre-loading search results
   * as the first chat exchange in a new research session.
   */
  createFromSearch(payload: {
    query: string;
    search_results: Record<string, unknown>[];
    search_metadata: Record<string, unknown>;
  }): ApiResponse<{
    session_id: string;
    user_chat_id: number;
    results_chat_id: number;
  }> {
    return this.postData('/from-search', payload);
  }

  /**
   * Update how many search results the user has viewed via "Show More" clicks.
   * The backend uses this count to determine how many rows to include
   * in the LLM context when the search turn appears in chat history.
   */
  updateViewedCount(chatId: number, viewedCount: number): ApiResponse {
    return this.patchData('/viewed-count', {
      chat_id: chatId,
      viewed_count: viewedCount,
    });
  }

  // Issue #428: Auto-filter - get AI-recommended search filters
  getAutoFilterSuggestion(payload: {
    query: string;
    filters?: any;
    history?: Array<{ query: string; answer: string }>;
  }): ApiResponse<AutoFilterResponse> {
    return this.postData('/auto-filter', payload);
  }
}

// Issue #428: Auto-filter response interface
interface AutoFilterResponse {
  search_legislation: boolean;
  search_decisions: boolean;
  decision_types: string[];
  confidence: number;
  reasoning: string;
}

export default new ResearchServices();
